<div class="customer-subscriptions">
  <!--
    CWS-1426 plan change. The membership's own card stays above the panel, read
    only: with several memberships on a customer, the panel alone gives no clue
    which one is being changed.
  -->
  <ng-container *ngIf="planChangeSubscriptionId as subscriptionId">
    <ng-container
      *ngIf="planChangeSubscription as selected"
      [ngTemplateOutlet]="card"
      [ngTemplateOutletContext]="{ $implicit: selected, readonly: true }"
    />
    <app-plan-change-panel
      [subscriptionId]="subscriptionId"
      [locationId]="locationId"
      [isActive]="true"
      [showBackButton]="false"
      (back)="closePlanChange()"
      (applied)="onPlanChangeApplied($event)"
    />
  </ng-container>

  <ng-container *ngIf="!planChangeSubscriptionId">
    <div class="customer-subscriptions__notice" *ngIf="planChangeNotice as notice" role="status">
      <span class="customer-subscriptions__notice-title">{{ notice.title }}</span>
      <span>{{ notice.body }}</span>
      <span class="customer-subscriptions__notice-upcoming" *ngIf="notice.upcoming">
        {{ notice.upcoming }}
      </span>
    </div>

    <!-- CWS-1780: what the last cancellation did; the cards below are already refreshed. -->
    <div
      class="customer-subscriptions__notice"
      *ngIf="cancelNotice as notice"
      role="status"
      data-testid="cancel-notice"
    >
      <span class="customer-subscriptions__notice-title">{{ notice.title }}</span>
      <span>{{ notice.body }}</span>
    </div>

    <p class="customer-subscriptions__error" *ngIf="loadError" role="alert">{{ loadError }}</p>

    <p class="customer-subscriptions__loading" *ngIf="isLoading" aria-live="polite">
      Refreshing subscriptions...
    </p>

    <ng-container *ngIf="liveSubscriptions.length > 0">
      <h3 class="customer-subscriptions__heading">{{ heading }}</h3>
      <ng-container
        *ngFor="let subscription of liveSubscriptions; trackBy: trackBySubscriptionId"
        [ngTemplateOutlet]="card"
        [ngTemplateOutletContext]="{ $implicit: subscription }"
      />
    </ng-container>

    <!-- The vehicle's most recent visit, for the membership shown above. -->
    <ng-container *ngIf="recentPurchase as recent">
      <h3 class="customer-subscriptions__eyebrow">Recent purchase</h3>
      <article class="recent-purchase">
        <div class="recent-purchase__title-row">
          <h4 class="recent-purchase__name">{{ recent.packageName }}</h4>
          <span class="recent-purchase__badge">Recent purchase</span>
        </div>
        <p class="recent-purchase__rewash" *ngIf="recentRewashLabel">{{ recentRewashLabel }}</p>
        <p class="recent-purchase__meta">Last Visit {{ recent.lastVisit }}</p>
        <div
          class="recent-purchase__rating"
          role="img"
          [attr.aria-label]="
            recent.feedbackRating !== null
              ? recent.feedbackRating + ' out of 5 stars'
              : 'No feedback rating'
          "
        >
          <span
            *ngFor="let star of starValues"
            class="recent-purchase__star"
            [class.recent-purchase__star--filled]="isStarFilled(recent, star)"
            aria-hidden="true"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 2.6l2.9 5.9 6.5.95-4.7 4.6 1.1 6.45L12 17.45 6.2 20.5l1.1-6.45-4.7-4.6 6.5-.95z"
              />
            </svg>
          </span>
        </div>
      </article>
    </ng-container>

    <ng-container *ngIf="pastSubscriptions.length > 0">
      <h3 class="customer-subscriptions__heading customer-subscriptions__heading--past">
        Past Subscriptions
      </h3>
      <ng-container
        *ngFor="let subscription of pastSubscriptions; trackBy: trackBySubscriptionId"
        [ngTemplateOutlet]="card"
        [ngTemplateOutletContext]="{ $implicit: subscription }"
      />
    </ng-container>

    <p class="customer-subscriptions__empty" *ngIf="subscriptions.length === 0 && !isLoading">
      This customer has no subscriptions.
    </p>
  </ng-container>
</div>

<!--
  CWS-1780: the shared cancel & refund panel (CWS-1778), keyed by the membership, not its
  sign-up order: a customer with several memberships cancels only the one chosen.
-->
<app-cancel-refund-panel
  *ngIf="cancelSubscription as selected"
  [isOpen]="true"
  [subscriptionId]="selected.subscription_id"
  [locationId]="locationId"
  intent="cancel"
  [customerName]="customerName"
  (closed)="closeCancel()"
  (done)="onCancelDone($event)"
/>

<app-pause-subscription-modal
  [visible]="!!pauseFlow.pauseTarget"
  [periodEnd]="pauseFlow.pauseTarget?.periodEnd ?? null"
  [timeZone]="pauseFlow.pauseTarget?.timeZone ?? null"
  [busy]="pauseFlow.busy"
  [errorCode]="pauseFlow.errorCode"
  (selectionChange)="pauseFlow.clearError()"
  (confirm)="pauseFlow.confirmPause($event)"
  (cancel)="pauseFlow.closePause()"
/>

<app-confirm-action-modal
  *ngIf="pauseFlow.cancelTarget"
  title="Cancel scheduled pause?"
  message="The membership will stay active and keep billing as normal."
  confirmLabel="Cancel pause"
  cancelLabel="Keep pause"
  busyLabel="Cancelling..."
  [busy]="pauseFlow.busy"
  testId="cancel-scheduled-pause-modal"
  (confirm)="pauseFlow.confirmCancelScheduled()"
  (cancel)="pauseFlow.dismissCancelScheduled()"
>
  <p class="customer-subscriptions__error" *ngIf="flowError" role="alert">{{ flowError }}</p>
</app-confirm-action-modal>

<app-confirm-action-modal
  *ngIf="pauseFlow.activateTarget"
  title="Activate now?"
  [message]="activateMessage"
  confirmLabel="Activate now"
  busyLabel="Activating..."
  [busy]="pauseFlow.busy"
  testId="activate-now-modal"
  (confirm)="pauseFlow.confirmActivate()"
  (cancel)="pauseFlow.dismissActivate()"
>
  <p class="customer-subscriptions__error" *ngIf="flowError" role="alert">{{ flowError }}</p>
</app-confirm-action-modal>

@if (paymentMethodSubject; as subject) {
  <app-payment-method-modal
    [subject]="subject"
    (updated)="onPaymentMethodUpdated($event)"
    (closed)="closePaymentMethod()"
  />
}

<app-subscription-result-modal
  [visible]="!!pauseFlow.result"
  [header]="pauseFlow.result?.header ?? ''"
  [title]="pauseFlow.result?.title ?? ''"
  [message]="pauseFlow.result?.message ?? ''"
  (close)="pauseFlow.closeResult()"
/>

<app-start-family-modal
  [target]="startFamilyFlow.target"
  [preview]="startFamilyFlow.preview"
  [loading]="startFamilyFlow.loading"
  [busy]="startFamilyFlow.busy"
  [reason]="startFamilyFlow.reason"
  (confirm)="startFamilyFlow.confirm()"
  (cancel)="startFamilyFlow.close()"
/>

<app-family-members-modal [flow]="membersFlow" />

<ng-template #card let-subscription let-readonly="readonly">
  <article
    *ngIf="viewState(subscription) as view"
    class="subscription-card"
    [class.subscription-card--past]="!subscription.is_live"
    [attr.data-subscription-id]="subscription.subscription_id"
  >
    <!--
      Card design: plan name with its status on the right; who and which car; the three wash
      stats as tiles; then price, member-since, billing and card in one line (CWS-1536, CWS-1571).
    -->
    <header class="subscription-card__header">
      <h4 class="subscription-card__name">{{ subscription.package_name }}</h4>
      <div class="subscription-card__tags">
        <span
          class="subscription-card__tag subscription-card__tag--way-plus"
          *ngIf="hasWayPlus(subscription)"
          data-testid="free-way-plus"
        >
          Free Way+
        </span>
        <!-- CWS-1734: a vendor import split one member across customer rows (CWS-1576). -->
        <span
          class="subscription-card__tag subscription-card__tag--linked"
          *ngIf="subscription.is_linked_member"
        >
          Linked member record
        </span>
        <!-- CWS-1582 §9: Family on a family plate, and Primary on the primary's. -->
        <span
          class="subscription-card__tag subscription-card__tag--family"
          data-testid="family-tag"
          *ngIf="familyTags(subscription).isFamily"
        >
          {{ familyTagLabel }}
        </span>
        <span
          class="subscription-card__tag subscription-card__tag--family"
          data-testid="family-primary-tag"
          *ngIf="familyTags(subscription).isPrimary"
        >
          {{ familyPrimaryLabel }}
        </span>
        <app-subscription-status-tag [status]="cardStatus(subscription)" />
      </div>
    </header>

    <dl class="subscription-card__identity">
      <div class="subscription-card__identity-item">
        <dt>Customer name</dt>
        <dd>{{ customerName || '—' }}</dd>
      </div>
      <div class="subscription-card__identity-item" data-testid="subscription-plate">
        <dt>License plate</dt>
        <!-- Space kept so a screen reader hears "9NAH283 · CA 2022 Porsche 911"; flex hides it. -->
        <dd ngPreserveWhitespaces>
          <span class="subscription-card__plate">{{ plateLabel(subscription) }}</span>
          <!-- "2022 Porsche 911" under the plate when the car is known. -->
          <span class="subscription-card__vehicle" *ngIf="vehicleLabel(subscription) as vehicle">{{
            vehicle
          }}</span>
        </dd>
      </div>
      <div class="subscription-card__identity-item">
        <dt>Phone number</dt>
        <dd>{{ customerPhone || '—' }}</dd>
      </div>
    </dl>

    <dl class="subscription-card__stats">
      <div class="subscription-card__stat">
        <dt>Washes available</dt>
        <dd>{{ washesAvailableLabel(subscription) }}</dd>
      </div>
      <!--
        All-time, not the current cycle: a plan change resets the cycle, and
        "0 redeemed" beside a real last-washed date reads as a bug.
      -->
      <div class="subscription-card__stat">
        <dt>Washes redeemed</dt>
        <dd>{{ subscription.washes_redeemed_total }}</dd>
      </div>
      <div class="subscription-card__stat" *ngIf="view.stat as stat; else lastWashed">
        <dt>{{ stat.key === 'resumes_on' ? 'Resumes on' : 'Pause starts' }}</dt>
        <dd>{{ stat.date | date: 'MMM d, y' }}</dd>
      </div>
      <ng-template #lastWashed>
        <div class="subscription-card__stat">
          <dt>Last washed</dt>
          <dd>{{ (subscription.last_washed_date | date: 'MMM d, y') || '—' }}</dd>
        </div>
      </ng-template>
    </dl>

    <!-- One line, dot-separated; wraps on the 570px drawer. Spaces kept for screen readers. -->
    <p class="subscription-card__meta" data-testid="subscription-meta" ngPreserveWhitespaces>
      <span class="subscription-card__price" *ngIf="hasPrice(subscription)"
        >{{ subscription.price | currency: 'USD' }}/mo</span
      >
      <span class="subscription-card__meta-item" data-testid="subscription-member-since"
        >Member since
        {{ (subscription.started_at | date: memberSinceFormat(subscription)) || '—' }}</span
      >
      <!-- A cancelling membership is not billed again: its period end is when it ends. -->
      <span
        class="subscription-card__meta-item"
        *ngIf="subscription.is_live; else ended"
        [attr.data-testid]="
          isCancelling(subscription) ? 'subscription-ends' : 'subscription-next-billing'
        "
        >{{ isCancelling(subscription) ? 'Ends' : 'Next billing' }}
        {{ (periodEndDate(subscription) | date: 'MMM d, y') || '—' }}</span
      >
      <ng-template #ended>
        <span class="subscription-card__meta-item" data-testid="subscription-ended"
          >Ended {{ (subscription.ended_at | date: 'MMM d, y') || '—' }}</span
        >
      </ng-template>
      <!-- CWS-1536: the card the membership renews on; current again after a change. -->
      <span
        class="subscription-card__meta-item"
        *ngIf="cardOnFileLabel(subscription) as cardOnFile"
        data-testid="card-on-file"
        >{{ cardOnFile }}</span
      >
      <span
        class="subscription-card__meta-item"
        *ngIf="subscription.last_payment_date"
        data-testid="subscription-last-payment"
        >Last payment {{ subscription.last_payment_date | date: 'MMM d, y' }}</span
      >
      <span class="subscription-card__meta-item" data-testid="subscription-source"
        >Source {{ sourceLabel(subscription) }}</span
      >
      <span
        class="subscription-card__meta-item"
        *ngIf="subscription.way_plus_status"
        data-testid="subscription-way-plus"
        [class.subscription-card__meta-item--accent]="hasWayPlus(subscription)"
        >Way+ {{ wayPlusLabel(subscription) }}</span
      >
      <span
        class="subscription-card__meta-item"
        *ngIf="subscription.member_code"
        data-testid="subscription-member-id"
        >Member ID {{ subscription.member_code }}</span
      >
    </p>

    <app-subscription-pause-notice [pause]="subscription.pause" />

    <footer
      class="subscription-card__footer"
      *ngIf="(view.manage.visible || view.showActivate) && !readonly"
    >
      <app-manage-subscription-menu
        *ngIf="view.manage.visible"
        [showUpdate]="view.manage.updateVisible"
        [showPause]="view.manage.pauseVisible"
        [showCancelScheduled]="view.manage.showCancelScheduled"
        [showCancel]="view.manage.cancelVisible"
        (update)="onUpdate(subscription)"
        (pause)="pauseFlow.openPause(pauseSubject(subscription))"
        (cancelScheduledPause)="pauseFlow.askCancelScheduled(pauseSubject(subscription))"
        (cancelMembership)="onCancelMembership(subscription)"
        [showPaymentMethod]="view.manage.paymentMethodVisible"
        (paymentMethod)="onPaymentMethod(subscription)"
        [showStartFamily]="view.manage.startFamilyVisible"
        (startFamily)="onStartFamily(subscription)"
      />
      <button
        *ngIf="view.showActivate"
        type="button"
        class="btn btn-primary-green btn-medium subscription-card__activate"
        data-testid="activate-now"
        [disabled]="pauseFlow.busy"
        (click)="pauseFlow.askActivate(pauseSubject(subscription))"
      >
        {{
          pauseFlow.busySubscriptionId === subscription.subscription_id
            ? 'Activating...'
            : 'Activate now'
        }}
      </button>
    </footer>
  </article>
</ng-template>
